import { useState, useCallback, useEffect, useRef } from 'react'
import { TrendingUp, Trophy, AlertTriangle, Star } from 'lucide-react'
import { useTickerEvents, useRealtimeTicker } from '../hooks/useTicker'
import './ToastAlerts.css'

const HIGH_IMPACT_TYPES = new Set([
  'lead_flip', 'match_tied',
  'segment_clinched_front', 'segment_clinched_back', 'segment_clinched_overall',
  'dormie_front', 'dormie_back', 'dormie_overall',
  'ace',
])

function getToastStyle(eventType) {
  if (eventType === 'ace') return { className: 'toast-ace', Icon: Star }
  if (eventType === 'lead_flip' || eventType === 'match_tied') return { className: 'toast-lead-flip', Icon: TrendingUp }
  if (eventType.startsWith('segment_clinched')) return { className: 'toast-clinch', Icon: Trophy }
  if (eventType.startsWith('dormie')) return { className: 'toast-dormie', Icon: AlertTriangle }
  return { className: 'toast-default', Icon: Star }
}

export default function ToastAlerts({ matchId }) {
  const [toasts, setToasts] = useState([])
  const seenIdsRef = useRef(new Set())
  const initialLoadRef = useRef(true)

  // Subscribe to realtime for query invalidation (triggers refetch of ticker events)
  useRealtimeTicker(matchId)

  // Fetch ticker events — same query the Ticker uses, stays in sync via realtime invalidation
  const { data: events } = useTickerEvents(matchId)

  const showToast = useCallback((event) => {
    if (!event || !HIGH_IMPACT_TYPES.has(event.event_type)) return

    const dedupeKey = `${event.game_id}-${event.event_type}-${event.hole_number}`
    if (seenIdsRef.current.has(dedupeKey)) return
    seenIdsRef.current.add(dedupeKey)
    setTimeout(() => seenIdsRef.current.delete(dedupeKey), 10000)

    const id = event.id || `${Date.now()}-${Math.random()}`
    setToasts((prev) => {
      const updated = prev.length >= 3 ? prev.slice(1) : prev
      return [...updated, { id, event, dismissing: false }]
    })

    setTimeout(() => {
      setToasts((prev) => prev.map((t) => t.id === id ? { ...t, dismissing: true } : t))
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, 300)
    }, 4000)
  }, [])

  // Detect new high-impact events from query data (covers cross-device via refetch)
  useEffect(() => {
    if (!events) return

    // On initial load, mark all existing events as seen (don't toast old events)
    if (initialLoadRef.current) {
      initialLoadRef.current = false
      for (const event of events) {
        if (HIGH_IMPACT_TYPES.has(event.event_type)) {
          seenIdsRef.current.add(`${event.game_id}-${event.event_type}-${event.hole_number}`)
        }
      }
      return
    }

    // Show toasts for any new high-impact events not yet seen
    for (const event of events) {
      showToast(event)
    }
  }, [events, showToast])

  // Listen for local scorer events (immediate, no network round-trip)
  useEffect(() => {
    const handler = (e) => showToast(e.detail)
    window.addEventListener('ticker-toast', handler)
    return () => window.removeEventListener('ticker-toast', handler)
  }, [showToast])

  if (toasts.length === 0) return null

  return (
    <div className="toast-container">
      {toasts.map((toast) => {
        const { className, Icon } = getToastStyle(toast.event.event_type)
        return (
          <div
            key={toast.id}
            className={`toast-banner ${className}${toast.dismissing ? ' toast-dismissing' : ''}`}
          >
            <Icon size={18} />
            <span className="toast-headline">{toast.event.headline}</span>
          </div>
        )
      })}
    </div>
  )
}
